import { API_BASE_URL, ENABLE_LIBRARY_DETERMINISTIC_API } from '../config';

function normalizeName(value = '') {
  return String(value).toLocaleLowerCase('tr-TR').trim();
}

function buildNameSet(items = []) {
  return new Set(items.map((item) => normalizeName(item)).filter(Boolean));
}

function getMissingList(recipe = {}) {
  const fromMatch = recipe?.match?.missingIngredients;
  if (Array.isArray(fromMatch)) return fromMatch;
  const fromFacts = recipe?.facts?.missingIngredients;
  if (Array.isArray(fromFacts)) return fromFacts;
  const fromRecipe = recipe?.missingIngredients;
  return Array.isArray(fromRecipe) ? fromRecipe : [];
}

function getRawMissingCount(recipe = {}) {
  const explicit =
    recipe?.match?.missingCount ??
    recipe?.facts?.missingCount ??
    recipe?.missingCount;
  const explicitNumber = Number(explicit);
  if (Number.isFinite(explicitNumber)) return Math.max(0, explicitNumber);

  const list = getMissingList(recipe);
  if (Array.isArray(list)) return list.length;
  return null;
}

function hasExplicitAvailabilitySignal(recipe = {}) {
  const rows = []
    .concat(Array.isArray(recipe?.scaledIngredients) ? recipe.scaledIngredients : [])
    .concat(Array.isArray(recipe?.malzemeler) ? recipe.malzemeler : [])
    .concat(Array.isArray(recipe?.ingredients) ? recipe.ingredients : []);

  return rows.some((ing) => (
    ing &&
    typeof ing === 'object' &&
    (
      ing?.isAvailable === true ||
      ing?.isAvailable === false ||
      ing?.available === true ||
      ing?.available === false ||
      ing?.inInventory === true ||
      ing?.inInventory === false ||
      ing?.hasStock === true ||
      ing?.hasStock === false
    )
  ));
}

function chooseMergedMatch(baseRecipe = {}, fullRecipe = {}, preferMatch = null) {
  if (preferMatch && typeof preferMatch === 'object') return preferMatch;

  const fullMatch = fullRecipe?.match && typeof fullRecipe.match === 'object' ? fullRecipe.match : null;
  const baseMatch = baseRecipe?.match && typeof baseRecipe.match === 'object' ? baseRecipe.match : null;

  if (!fullMatch) return baseMatch;
  if (!baseMatch) return fullMatch;

  const baseMissing = getRawMissingCount(baseRecipe);
  const fullMissing = getRawMissingCount(fullRecipe);
  const baseMissingListCount = getMissingList(baseRecipe).length;
  const fullMissingListCount = getMissingList(fullRecipe).length;
  const fullHasAvailabilitySignal = hasExplicitAvailabilitySignal(fullRecipe);

  const baseShowsMissing = (Number.isFinite(baseMissing) && baseMissing > 0) || baseMissingListCount > 0;
  const fullLooksWeakEmpty = Number.isFinite(fullMissing) &&
    fullMissing === 0 &&
    fullMissingListCount === 0 &&
    !fullHasAvailabilitySignal;

  if (baseShowsMissing && fullLooksWeakEmpty) {
    // Keep compact-card match when detail payload has no trustworthy availability/missing signal.
    return baseMatch;
  }

  return fullMatch;
}

function mergeMatchData(baseRecipe = {}, fullRecipe = {}, preferMatch = null) {
  const mergedMatch = chooseMergedMatch(baseRecipe, fullRecipe, preferMatch);
  const mergedMissingCount = Number(mergedMatch?.missingCount);
  const missingCount = Number.isFinite(mergedMissingCount)
    ? Math.max(0, mergedMissingCount)
    : (
      getRawMissingCount(fullRecipe) ??
      getRawMissingCount(baseRecipe) ??
      0
    );
  const missingIngredients = Array.isArray(mergedMatch?.missingIngredients)
    ? mergedMatch.missingIngredients
    : (
      getMissingList(fullRecipe).length > 0
        ? getMissingList(fullRecipe)
        : getMissingList(baseRecipe)
    );

  return {
    match: mergedMatch,
    matchPercentage:
      mergedMatch?.matchPercentage ??
      fullRecipe.matchPercentage ??
      baseRecipe.matchPercentage,
    missingCount,
    missingIngredients
  };
}

export async function hydrateRecipeForModal(recipe, options = {}) {
  if (!recipe) return recipe;

  const {
    forceFullFetch = false,
    skipFullFetch = false,
    preferMatch = null,
    pantryIngredients = [],
    targetPortion = null
  } = options;
  const pantrySet = buildNameSet(pantryIngredients);
  const requestedPortion = Number(targetPortion || recipe?.targetPortion || recipe?.presentation?.appliedPortion || 0);
  // [B14-F] 0.5 adımı koru (composer mealPortion 1.5 → 2 yuvarlanmasın; backend kesirli porsiyonu destekliyor).
  const safeRequestedPortion = Number.isFinite(requestedPortion) && requestedPortion > 0 ? Math.round(requestedPortion * 2) / 2 : null;

  const compactIngredients =
    (Array.isArray(recipe.malzemeler) && recipe.malzemeler.length > 0)
      ? recipe.malzemeler
      : (recipe.ingredients || []);

  const hasStringIngredients = compactIngredients.some((ing) => typeof ing === 'string');
  const missingMirrorMetadata = compactIngredients.some((ing) => {
    if (!ing || typeof ing !== 'object') return false;
    const unit = String(ing.unit || ing.birim || '').toLowerCase().trim();
    const isTechnicalOnly = ['g', 'gr', 'kg', 'ml', 'lt'].includes(unit);
    return isTechnicalOnly && !ing.originalText && !ing.turkmutfagi && !ing.amountRaw && !ing.unitRaw;
  });

  const compactSnapshotBreakdownCount = Array.isArray(recipe?.priceSnapshot?.breakdown)
    ? recipe.priceSnapshot.breakdown.length
    : 0;
  const compactCostBreakdownCount = Array.isArray(recipe?.costBreakdown)
    ? recipe.costBreakdown.length
    : 0;
  const compactHasUsablePriceSnapshot = !!(
    recipe?.priceSnapshot &&
    (
      Number.isFinite(Number(recipe?.priceSnapshot?.total)) ||
      compactSnapshotBreakdownCount > 0
    )
  );
  const compactHasAnyCostRows = compactSnapshotBreakdownCount > 0 || compactCostBreakdownCount > 0;

  const needsFullFetch =
    !!forceFullFetch ||
    !!recipe?.isMatrix ||
    hasStringIngredients ||
    (!recipe?.malzemeler?.length && !recipe?.ingredients?.length) ||
    missingMirrorMetadata ||
    !compactHasUsablePriceSnapshot ||
    !compactHasAnyCostRows;

  if (skipFullFetch || (!needsFullFetch || !recipe?.id)) {
    const merged = mergeMatchData(recipe, recipe, preferMatch);
    return {
      ...recipe,
      ...merged
    };
  }

  try {
    const token = localStorage.getItem('authToken');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    let full = null;

    // Library cards should prefer deterministic library detail endpoint.
    if (ENABLE_LIBRARY_DETERMINISTIC_API && recipe?.sourceOfTruth === 'library_catalog') {
      const qs = new URLSearchParams({ _ts: String(Date.now()) });
      if (safeRequestedPortion) qs.set('portion', String(safeRequestedPortion));
      const libRes = await fetch(`${API_BASE_URL}/api/recipes/library/${recipe.id}?${qs.toString()}`, {
        cache: 'no-store',
        headers
      });
      if (libRes.ok) {
        full = await libRes.json();
      }
    }

    if (!full) {
      const qs = new URLSearchParams({ _ts: String(Date.now()) });
      if (safeRequestedPortion) qs.set('portion', String(safeRequestedPortion));
      const res = await fetch(`${API_BASE_URL}/api/recipes/${recipe.id}?${qs.toString()}`, {
        cache: 'no-store',
        headers
      });

      if (!res.ok) {
        const merged = mergeMatchData(recipe, recipe, preferMatch);
        return { ...recipe, ...merged };
      }
      full = await res.json();
    }
    const preservedPresentation = {
      ...(recipe.presentation || {}),
      ...(full.presentation || {})
    };

    const resolvedAppliedPortion =
      safeRequestedPortion ||
      Number(full?.presentation?.appliedPortion) ||
      Number(full?.targetPortion) ||
      Number(recipe?.presentation?.appliedPortion) ||
      Number(recipe?.targetPortion) ||
      1;

    const fullIngredients = (Array.isArray(full.malzemeler) && full.malzemeler.length > 0)
      ? full.malzemeler
      : (full.ingredients || []);

    const preservedIngredients = fullIngredients.length > 0
      ? fullIngredients.map((fullIng) => {
          const compactIng = compactIngredients.find((candidate) =>
            (candidate?.dictionaryId && fullIng?.dictionaryId && String(candidate.dictionaryId) === String(fullIng.dictionaryId)) ||
            (candidate?.id && fullIng?.id && String(candidate.id) === String(fullIng.id)) ||
            (candidate?.name && fullIng?.name && normalizeName(candidate.name) === normalizeName(fullIng.name))
          );

          const ingredientName =
            fullIng?.name ||
            fullIng?.ingredient_name ||
            fullIng?.ingredientName ||
            fullIng?.ingredient?.name ||
            '';
          const pantryAvailable = pantrySet.size > 0 && pantrySet.has(normalizeName(ingredientName));

          // [Fix #2] Modal (full = /library/:id, kullanıcı stoğuyla availability hesaplar)
          // OTORİTEDİR; kart (compact, liste-cache'i) değerini EZMESİN. Önceden compactIng
          // önce geliyordu → kart "hazır" derse modalın doğru "eksik"i (Sucuk vb.) eziliyordu.
          const isAvailable =
            fullIng?.isAvailable ??
            compactIng?.isAvailable ??
            // Do not force explicit false for non-matching rows; keep undefined
            // unless backend already provided availability.
            (pantrySet.size > 0 ? (pantryAvailable ? true : undefined) : undefined);

          return {
            ...fullIng,
            ...(isAvailable === undefined ? {} : { isAvailable })
          };
        })
      : compactIngredients;

    const resolvedIngredients =
      preservedIngredients.length > 0
        ? preservedIngredients
        : [];

    const merged = mergeMatchData(recipe, full, preferMatch);

    const scaledFromFull = Array.isArray(full.scaledIngredients) ? full.scaledIngredients : [];
    const resolvedScaledIngredients = scaledFromFull.length > 1 ? scaledFromFull : resolvedIngredients;

    const fullSnapshotBreakdownCount = Array.isArray(full?.priceSnapshot?.breakdown)
      ? full.priceSnapshot.breakdown.length
      : 0;
    const fullHasUsableSnapshot = !!(
      full?.priceSnapshot &&
      (
        Number.isFinite(Number(full?.priceSnapshot?.total)) ||
        fullSnapshotBreakdownCount > 0
      )
    );
    const compactHasBreakdown = compactSnapshotBreakdownCount > 0;
    const fullHasBreakdown = fullSnapshotBreakdownCount > 0;

    const mergedCostBreakdown =
      (Array.isArray(full?.costBreakdown) && full.costBreakdown.length > 0)
        ? full.costBreakdown
        : (fullHasBreakdown
          ? full.priceSnapshot.breakdown
          : (compactHasBreakdown
            ? recipe.priceSnapshot.breakdown
            : (Array.isArray(recipe?.costBreakdown) ? recipe.costBreakdown : [])));

    let mergedPriceSnapshot = fullHasUsableSnapshot
      ? full.priceSnapshot
      : (compactHasUsablePriceSnapshot ? recipe.priceSnapshot : null);

    if (!mergedPriceSnapshot && mergedCostBreakdown.length > 0) {
      const fallbackTotal = mergedCostBreakdown.reduce(
        (sum, line) => sum + (Number(line?.total) || 0),
        0
      );
      mergedPriceSnapshot = {
        total: fallbackTotal,
        currency: 'TRY',
        portionBasis:
          Number(full?.targetPortion) ||
          Number(full?.presentation?.appliedPortion) ||
          Number(recipe?.targetPortion) ||
          Number(recipe?.presentation?.appliedPortion) ||
          Number(full?.servings) ||
          Number(recipe?.servings) ||
          1,
        source: 'HYDRATE_FALLBACK',
        breakdown: mergedCostBreakdown
      };
    }

    return {
      ...full,
      ...merged,
      malzemeler: resolvedIngredients,
      ingredients: resolvedIngredients,
      scaledIngredients: resolvedScaledIngredients,
      presentation: {
        ...preservedPresentation,
        appliedPortion: resolvedAppliedPortion,
        baseServings: 1
      },
      isPreScaled: !!(recipe.isPreScaled || recipe.presentation?.isPreScaled || full.isPreScaled || full.presentation?.isPreScaled),
      totalCost: full.totalCost ?? recipe.totalCost,
      costBreakdown: mergedCostBreakdown,
      priceSnapshot: mergedPriceSnapshot,
      costMeta: recipe.costMeta || full.costMeta,
      targetPortion: resolvedAppliedPortion,
      servings: 1,
      calories: recipe.calories ?? full.calories,
      totalCalories: recipe.totalCalories ?? full.totalCalories,
      nutritionalValues: recipe.nutritionalValues || full.nutritionalValues
    };
  } catch (error) {
    console.warn('[RecipeModalData] Full recipe fetch failed, compact recipe used:', error.message);
    const merged = mergeMatchData(recipe, recipe, preferMatch);
    return { ...recipe, ...merged };
  }
}
